import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Camera, Loader2, X } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { toast } from 'sonner'
import {
  useCurrentUserProfile,
  useUpdateCurrentProfile,
  useUpdateCurrentUser,
} from '@/entities/employee/model/queries'
import { useCompleteOnboarding } from '@/entities/profile'
import { OnboardingLayout } from './OnboardingLayout'

type ProfileBasicsForm = {
  firstName: string
  lastName: string
  mobileNumber: string
  dateOfBirth: string
  gender: string
  address: string
  emergencyContactName: string
  emergencyContactPhone: string
}

const formatDateForInput = (value: string | null): string => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime()) || date.getFullYear() < 1900) return ''
  return date.toISOString().split('T')[0]
}

const inputClass =
  'h-12 rounded-xl border-line bg-surface-mute/50 text-[15px] transition-colors hover:bg-surface-mute focus:bg-surface focus-visible:ring-indigo-600'

export function ProfileBasicsCard({ onDone }: { onDone: () => void }) {
  const { data: profile } = useCurrentUserProfile()
  const updateProfile = useUpdateCurrentProfile(profile?.userId ?? 0)
  const updateUser = useUpdateCurrentUser()
  const completeOnboarding = useCompleteOnboarding()

  const [submitting, setSubmitting] = useState(false)
  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<ProfileBasicsForm>({
    defaultValues: {
      firstName: '',
      lastName: '',
      mobileNumber: '',
      dateOfBirth: '',
      gender: '',
      address: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
    },
  })

  useEffect(() => {
    if (profile) {
      form.reset({
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        mobileNumber: profile.mobileNumber ?? '',
        dateOfBirth: formatDateForInput(profile.dateOfBirth),
        gender: profile.gender ?? '',
        address: profile.address ?? '',
        emergencyContactName: profile.emergencyContactName ?? '',
        emergencyContactPhone: profile.emergencyContactPhone ?? '',
      })
    }
  }, [profile, form])

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) return
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller.')
      return
    }
    setProfileImage(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const handleRemoveImage = () => {
    setProfileImage(null)
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImagePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const finish = async (persist: boolean) => {
    setSubmitting(true)
    try {
      if (persist) {
        const values = form.getValues()
        if (form.formState.isDirty || profileImage) {
          await updateProfile.mutateAsync({
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth || null,
            gender: values.gender || null,
            address: values.address || null,
            emergencyContactName: values.emergencyContactName || null,
            emergencyContactPhone: values.emergencyContactPhone || null,
            profileImage,
          })
        }
        if (values.mobileNumber !== (profile?.mobileNumber ?? '')) {
          await updateUser.mutateAsync({ mobileNumber: values.mobileNumber })
        }
      }
      await completeOnboarding.mutateAsync()
      onDone()
    } catch {
      toast.error('Could not save your profile. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const initials = `${profile?.firstName?.[0] ?? ''}${profile?.lastName?.[0] ?? ''}`

  return (
    <OnboardingLayout
      step={2}
      totalSteps={2}
      eyebrow="Your profile"
      title="Tell us about you"
      subtitle="A few details so your team knows who you are. You can change these anytime."
    >
      <div className="max-h-[52vh] space-y-5 overflow-y-auto pr-1">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar className="h-16 w-16 border-2 border-line">
              <AvatarImage src={imagePreview ?? profile?.profilePictureUrl ?? undefined} />
              <AvatarFallback className="bg-indigo-100 text-lg text-indigo-700">
                {initials}
              </AvatarFallback>
            </Avatar>
            {imagePreview && (
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -right-1 -top-1 rounded-full bg-red-500 p-1 text-white transition-colors hover:bg-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="flex-1 space-y-1.5">
            <Label>Profile picture</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="mr-2 h-4 w-4" />
              {imagePreview ? 'Change photo' : 'Upload photo'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <p className="text-xs text-ink-3">JPG, PNG or GIF. Max 5MB.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <Input id="firstName" className={inputClass} {...form.register('firstName')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last name</Label>
            <Input id="lastName" className={inputClass} {...form.register('lastName')} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="mobileNumber">Mobile number</Label>
            <Input
              id="mobileNumber"
              type="tel"
              className={inputClass}
              {...form.register('mobileNumber')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dateOfBirth">Date of birth</Label>
            <Input
              id="dateOfBirth"
              type="date"
              className={inputClass}
              {...form.register('dateOfBirth')}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gender">Gender</Label>
            <Select
              value={form.watch('gender')}
              onValueChange={(value) => form.setValue('gender', value, { shouldDirty: true })}
            >
              <SelectTrigger
                id="gender"
                className="h-12 rounded-xl border-line bg-surface-mute/50 focus-visible:ring-indigo-600"
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="emergencyContactName">Emergency contact</Label>
            <Input
              id="emergencyContactName"
              className={inputClass}
              {...form.register('emergencyContactName')}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="emergencyContactPhone">Emergency phone</Label>
            <Input
              id="emergencyContactPhone"
              type="tel"
              className={inputClass}
              {...form.register('emergencyContactPhone')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              rows={1}
              className="min-h-12 rounded-xl border-line bg-surface-mute/50 text-[15px] focus-visible:ring-indigo-600"
              {...form.register('address')}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          className="text-ink-3 hover:text-ink"
          onClick={() => finish(false)}
          disabled={submitting}
        >
          Skip for now
        </Button>
        <Button
          onClick={() => finish(true)}
          disabled={submitting}
          className="h-12 rounded-xl bg-indigo-600 px-6 text-[15px] font-semibold text-white shadow-md shadow-indigo-600/20 transition-all hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-600/30 active:scale-[0.98]"
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save &amp; continue
        </Button>
      </div>
    </OnboardingLayout>
  )
}
